import React, { useState, useRef, useEffect } from "react";
import "./ChatWidget.scss";
import ChatBotIcon from "../../../../assets/icons/chatbot-icon.png";

const API_BASE_URL = "https://test.workbrook.us/api/v1/chatbot";
const DEFAULT_USER_ID = "124";


const showToast = (message, type = "error") => {
  const toast = document.createElement("div");
  toast.className = `toast toast-${type}`;
  toast.textContent = message;
  toast.style.cssText = `
    position: fixed;
    top: 20px;
    right: 20px;
    background: ${type === "error" ? "#ef4444" : "#10b981"};
    color: white;
    padding: 12px 24px;
    border-radius: 8px;
    z-index: 10000;
    font-size: 14px;
    box-shadow: 0 4px 12px rgba(0,0,0,0.15);
    animation: slideIn 0.3s ease-out;
  `;

  const style = document.createElement("style");
  style.textContent = `
    @keyframes slideIn {
      from { transform: translateX(100%); opacity: 0; }
      to { transform: translateX(0); opacity: 1; }
    }
  `;
  document.head.appendChild(style);
  document.body.appendChild(toast);

  setTimeout(() => {
    toast.remove();
    style.remove();
  }, 4000);
};

/* ---------- Storage Helpers ---------- */
const transformMessagesToHistory = (messages) => {
  return messages
    .filter((msg) => msg.id !== 1)
    .reduce((acc, msg, index, array) => {
      if (msg.from === "user") {
        const botResponse = array.find(
          (m, idx) => idx > index && m.from === "bot"
        );
        if (botResponse) {
          acc.push({
            question: msg.text,
            answer: botResponse.text,
          });
        }
      }
      return acc;
    }, []);
};

const saveChatHistory = (messages) => {
  const chatHistory = transformMessagesToHistory(messages);
  localStorage.setItem("chat_history", JSON.stringify(chatHistory));
  return chatHistory;
};

const loadChatHistory = () => {
  try {
    const stored = localStorage.getItem("chat_history");
    if (stored) {
      const chatHistory = JSON.parse(stored);
      return chatHistory.flatMap((item, index) => [
        { id: index * 2 + 2, from: "user", text: item.question },
        { id: index * 2 + 3, from: "bot", text: item.answer },
      ]);
    }
  } catch (error) {
    console.error("Error loading chat history:", error);
  }
  return [];
};

/* ---------- API Helpers ---------- */
const sendEndChat = async (messages, userId = DEFAULT_USER_ID) => {
  const chatHistory = transformMessagesToHistory(messages);
  if (chatHistory.length === 0) return;

  try {
    const response = await fetch(`${API_BASE_URL}/end-chat/${userId}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ chatHistory }),
    });

    if (response.ok) {
      localStorage.removeItem("chat_history");
      console.log("End chat request successful");
    } else {
      console.error(
        "Failed to send end chat request:",
        response.status,
        await response.text()
      );
    }
  } catch (error) {
    console.error("Error sending end chat request:", error);
  }
};

const sendChatBeacon = (messages, userId = DEFAULT_USER_ID) => {
  const chatHistory = transformMessagesToHistory(messages);
  if (chatHistory.length === 0) return;

  const blob = new Blob([JSON.stringify({ chatHistory })], {
    type: "application/json",
  });
  navigator.sendBeacon(`${API_BASE_URL}/end-chat/${userId}`, blob);
};


const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [messages, setMessages] = useState(() => [
    {
      id: 1,
      from: "bot",
      text: "👋 Hi there! What would you like us to help you with today?",
    },
    ...loadChatHistory(),
  ]);
  const [lastActivity, setLastActivity] = useState(Date.now());



  const bodyRef = useRef(null);
  const inactivityTimerRef = useRef(null);


  const handleMinimize = () => {
    setIsMinimized(true);
    setOpen(false);
  };

  const handleClose = () => setShowConfirmDialog(true);

  const handleConfirmClose = async (shouldEndChat) => {
    if (shouldEndChat) await sendEndChat(messages);
    setShowConfirmDialog(false);
    setOpen(false);
    setIsMinimized(false);
  };

  const handleSend = async () => {
    const trimmed = input.trim();
    if (!trimmed || isLoading) return;

    const userMessage = { id: Date.now(), from: "user", text: trimmed };
    setMessages((prev) => [...prev, userMessage]);
    setInput("");
    setIsLoading(true);
    setLastActivity(Date.now());

    try {
      const currentChatHistory = saveChatHistory([...messages, userMessage]);
      const response = await fetch(`${API_BASE_URL}/answer`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          question: trimmed,
          chatHistory: currentChatHistory,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      const botMessage = {
        id: Date.now() + 1,
        from: "bot",
        text: data?.answer || "I don't know that yet.",
      };

      setMessages((prev) => [...prev, botMessage]);
      setTimeout(
        () => saveChatHistory([...messages, userMessage, botMessage]),
        100
      );
    } catch (error) {
      const errorMsg = {
        id: Date.now() + 1,
        from: "bot",
        text: "Sorry, I'm having trouble responding. Please try again later.",
      };
      setMessages((prev) => [...prev, errorMsg]);
      showToast(error.message || "Failed to send message", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Effects
  useEffect(() => {
    if (bodyRef.current) {
      bodyRef.current.scrollTo({
        top: bodyRef.current.scrollHeight,
        behavior: "smooth",
      });
    }
  }, [messages, open]);

  useEffect(() => {
    if (messages.length > 1) {
      saveChatHistory(messages);
    }
  }, [messages]);

  useEffect(() => {
    const resetTimer = () => {
      clearTimeout(inactivityTimerRef.current);
      setLastActivity(Date.now());
    };

    const handleActivity = () => resetTimer();

    window.addEventListener("mousemove", handleActivity);
    window.addEventListener("keypress", handleActivity);
    window.addEventListener("click", handleActivity);

    return () => {
      window.removeEventListener("mousemove", handleActivity);
      window.removeEventListener("keypress", handleActivity);
      window.removeEventListener("click", handleActivity);
    };
  }, [messages]);

  useEffect(() => {
    const handleUnload = () => sendChatBeacon(messages);
    const handleVisibilityChange = () => {
      if (document.hidden) sendChatBeacon(messages);
    };

    window.addEventListener("beforeunload", handleUnload);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.removeEventListener("beforeunload", handleUnload);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [messages]);


  const unreadCount = messages.length > 1 && !open ? 1 : 0;

  return (
    <>
      <aside
        className={`wb-chatbox ${open ? "open" : ""} ${
          isMinimized ? "minimized" : ""
        }`}
        role="dialog"
        onClick={() => {
          if (isMinimized) {
            setIsMinimized(false);
            setOpen(true);
          }
        }}
      >
        <header className="wb-chatbox__header">
          <div className="avatar">
            <img className="chatbot-icon" src={ChatBotIcon} alt="chatbot-icon" />
          </div>
          <div className="info">
            <div className="name">Workbrook assistant</div>
            <small>Ask me anything about your account</small>
          </div>
          <div className="wb-chatbox__header-actions">
            <button
              className="wb-chatbox__header-btn wb-chatbox__minimize-btn"
              onClick={(e) => {
                e.stopPropagation();
                handleMinimize();
              }}
              aria-label="Minimize chat"
            >
              −
            </button>
            <button
              className="wb-chatbox__header-btn wb-chatbox__close-btn"
              onClick={(e) => {
                e.stopPropagation();
                handleClose();
              }}
              aria-label="Close chat"
            >
              ×
            </button>
          </div>
        </header>

        <main ref={bodyRef} className="wb-chatbox__body">
          {messages.map((m) => (
            <div key={m.id} className={m.from}>
              {m.text}
              {m.from === "bot" &&
                m.id === messages[messages.length - 1]?.id &&
                isLoading && <span className="typing-indicator">...</span>}
            </div>
          ))}
          {isLoading && (
            <div className="bot">
              <span className="typing-indicator">Assistant is typing...</span>
            </div>
          )}
        </main>

        <footer className="wb-chatbox__footer">
          <textarea
            placeholder="Type your message…"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={isLoading}
          />
          <button
            onClick={handleSend}
            disabled={!input.trim() || isLoading}
            aria-label="Send message"
          >
            {isLoading ? <span className="sending-indicator">⏳</span> : "➤"}
          </button>
        </footer>
      </aside>

      <button
        className="wb-fab"
        aria-label={open ? "Close chat" : "Open chat"}
        onClick={() =>
          isMinimized ? (setIsMinimized(false), setOpen(true)) : setOpen(!open)
        }
      >
        <img className="chatbot-icon" src={ChatBotIcon} alt="chatbot-icon" />
        {unreadCount > 0 && <span className="badge">!</span>}
      </button>

      {showConfirmDialog && (
        <>
          <div className="wb-chatbox__overlay" />
          <div className="wb-chatbox__confirm-dialog">
            <p>Do you want to end this chat session?</p>
            <div className="wb-chatbox__confirm-btns">
              <button
                className="wb-chatbox__confirm-btn wb-chatbox__confirm-btn--yes"
                onClick={() => handleConfirmClose(true)}
              >
                Yes
              </button>
              <button
                className="wb-chatbox__confirm-btn wb-chatbox__confirm-btn--no"
                onClick={() => handleConfirmClose(false)}
              >
                No
              </button>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default ChatWidget;
